<template>
	<highcharts
		class="highcharts-container"
		tabindex="0"
		:options="chartOptions"
		:callback="onChartLoaded"
	></highcharts>
</template>

<script setup lang="ts">
import { AxisLabelsFormatterContextObject, Point as ChartPoint, Series as ChartSeries } from 'highcharts';
import { BarChartDataSeries, BarChartDataSeriesPoint, BarChartValueFormat } from 'types/charts/bar-chart';
import { computed, ref, watch } from 'vue';
import BaseChart from '@charts/BaseChartOptions';
import deepmerge from 'deepmerge';
import { formattedPerformancePercentageValue } from '@utils/performance-portfolio';
import { Chart as highcharts } from 'highcharts-vue';
import { stringToShortenedCurrency } from '@filters/shared-filters';
import { YAxisBounds } from 'types/charts';
import type { Options as ChartOptions, SeriesOptionsType } from 'highcharts';

interface Props {
	chartSeries: BarChartDataSeries;
	valueFormat?: BarChartValueFormat;
	behavior?: 'normal' | 'select-all';
	customOptions?: ChartOptions;
	yAxisBounds?: YAxisBounds;
	reflowChart?: boolean;
}

interface Emits {
	(e: 'mouseOver', value: string): void;
	(e: 'mouseOut'): void;
}

const props = withDefaults(defineProps<Props>(), {
	valueFormat: 'normal',
	behavior: 'normal',
	customOptions: () => ({}),
	yAxisBounds: () => ({ yAxisMin: null, yAxisMax: null }),
	reflowChart: false
});

const emits = defineEmits<Emits>();

const chart = ref<Highcharts.Chart | null>(null);

const _hoverCache = ref<number | null>(null);

const seriesData = computed((): Array<BarChartDataSeriesPoint> => props.chartSeries.data);

const series = computed((): Array<SeriesOptionsType> => {
	return [
		{
			id: 'hover',
			type: 'column',
			color: 'transparent',
			data: seriesData.value.map(() => 0),
			minPointLength: 100000,
			states: {
				select: { color: 'transparent' },
				hover: { color: 'transparent' }
			}
		},
		{
			type: 'column',
			color: props.chartSeries.color,
			data: seriesData.value.map((point: BarChartDataSeriesPoint) => ({
				y: point.value,
				className: point.classes?.join(' ')
			})),
			minPointLength: 4,
			states: {
				select: {
					color: props.chartSeries.activeColor
				},
				hover: {
					color: props.chartSeries.activeColor,
					opacity: 1
				}
			}
		}
	];
});

const chartOptions = computed((): ChartOptions => {
	const options: ChartOptions = {
		chart: {
			type: 'column',
			events: {
				redraw: (event) => onChartChange(event)
			},
			style: {
				fontSize: '14px',
				lineHeight: 1.5,
				fontWeight: '600',
				color: '#373633'
			},
			animation: {
				duration: 100
			}
		},
		plotOptions: {
			column: {
				borderWidth: 0,
				dataLabels: { enabled: false },
				stacking: 'normal',
				states: {
					hover: { enabled: true },
					inactive: { enabled: false }
				},
				stickyTracking: true
			},
			series: {
				allowPointSelect: false,
				point: {
					events: { mouseOver: onMouseOver, mouseOut: onMouseOut }
				}
			}
		},
		legend: { enabled: false },
		series: series.value,
		tooltip: {
			animation: false,
			borderWidth: 0,
			enabled: true,
			shape: 'square',
			shadow: false,
			useHTML: true,
			padding: 0,
			formatter(): string {
				return `<div class="text-align-center">${seriesData.value[this.point.x].key}`;
			},
			hideDelay: 0
		},
		responsive: {
			rules: [
				{
					condition: {
						minWidth: 480
					},
					chartOptions: {
						tooltip: {
							positioner(labelWidth, labelHeight, point): Highcharts.PositionObject {
								return {
									x: this.chart.plotLeft + point.plotX - labelWidth / 2,
									y: 0 - labelHeight / 4
								};
							}
						},
						xAxis: {
							labels: {
								useHTML: true,
								formatter(): string {
									return this.value.toString().replaceAll(' ', '&nbsp;');
								}
							}
						}
					}
				}
			]
		},
		xAxis: {
			categories: seriesData.value.map((data) => data.label),
			crosshair: {
				color: '#6B6A65',
				width: 1
			},
			labels: {
				enabled: true,
				step: 1,
				rotation: 0,
				formatter(): string {
					return this.value.toString();
				}
			},
			lineWidth: 0,
			offset: 12
		},
		yAxis: {
			min: props.yAxisBounds.yAxisMin,
			max: props.yAxisBounds.yAxisMax,
			offset: 12,
			labels: {
				style: {
					letterSpacing: '1.5px'
				},
				formatter: formattedLabel
			},
			gridLineColor: '#E4E3DF',
			gridLineDashStyle: 'Dash',
			plotLines: [
				{
					value: 0,
					width: 1.5,
					color: '#373633',
					zIndex: 10,
					dashStyle: 'ShortDash'
				}
			]
		}
	};

	const combinedOptions = deepmerge(options, props.customOptions);

	return deepmerge(new BaseChart().getOptions(), combinedOptions);
});

watch(
	() => props.reflowChart,
	(reflowChart: boolean): void => {
		if (reflowChart) {
			chart.value?.reflow();
		}
	}
);

function onChartLoaded(newChart: Highcharts.Chart): void {
	chart.value = newChart;
	applyDefaultBehavior();
}

function onChartChange(event: Event): void {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	chart.value = event.target as any as Highcharts.Chart;
	applyDefaultBehavior();
}

function onMouseOver(event: Event): void {
	const point = event.target as unknown as ChartPoint;

	if (point.x !== _hoverCache.value) {
		emits('mouseOver', seriesData.value[point.x].key);
	}

	chart.value?.series?.forEach((serie: ChartSeries) => {
		serie?.data.forEach((dataPoint) => {
			dataPoint?.select(false, true);
			dataPoint?.setState(dataPoint.x === point.x ? 'hover' : 'normal');
		});
	});

	_hoverCache.value = point.x;
}

function onMouseOut(): void {
	_hoverCache.value = null;
	applyDefaultBehavior();

	emits('mouseOut');
}

function formattedLabel(axis: AxisLabelsFormatterContextObject<string>): string {
	const value = axis.value.toString();

	switch (props.valueFormat) {
		case 'currency':
			return stringToShortenedCurrency(value, 0);
		case 'percentage': {
			const percentageValue = formattedPerformancePercentageValue(value);
			const decimalValue = Number(percentageValue) % 1 === 0 ? 0 : 1;

			return formattedPerformancePercentageValue(value, true, true, decimalValue);
		}
		case 'normal':
		default:
			return value;
	}
}

function applyDefaultBehavior(): void {
	if (props.behavior === 'select-all') {
		selectAll();
	}
}

function selectAll(): void {
	chart.value?.series?.forEach((serie: ChartSeries) =>
		serie?.data.forEach((data: ChartPoint) => data?.select(true, true))
	);
}
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors.scss' as *;
@use 'sass:map';

:deep(.highcharts-container),
:deep(.highcharts-container svg) {
	width: 100% !important;
	height: 100% !important;
}

:deep(.highcharts-tooltip > span) {
	background-color: map.get($validation-colors, 'info-tint');
	padding: 3px 12px;
	border-radius: 12px;
}

:deep(.highcharts-point) {
	&.reduced-opacity {
		opacity: 0.6;
	}
}
</style>
